import React from "react";
import { Link } from "react-router-dom";
import { PublicHeader } from "@/components/PublicHeader";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { SEO_WHY } from "@/lib/seo-defs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, CheckCircle2 } from "lucide-react";

export default function WhyPage() {
  return (
    <div className="min-h-screen">
      <SEO {...SEO_WHY} />
      <PublicHeader />
      <section className="pt-28 pb-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Badge className="bg-[#EAF0FF] text-[#2D6BFF] border-0">Perché CXOORA</Badge>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Un manager esperto, senza il peso di un'assunzione full-time.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            CXOORA è una piattaforma di Leadership as a Service. Permette alle PMI italiane di inserire competenze direzionali esattamente dove servono, per il tempo realmente necessario, con risultati misurabili.
          </p>
        </div>
      </section>

      <section className="py-12 bg-[#F6F8FC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-10">
          {[
            { t: "Quali problemi risolve il Fractional Management", c: "Carenza di competenze direzionali a tempo pieno, difficoltà nell'organizzare la crescita, costi non sostenibili di assunzione full-time, consulenze teoriche poco operative. Il Fractional Manager entra nel team e supporta concretamente l'esecuzione." },
            { t: "Perché è utile alle PMI", c: "Le PMI raramente possono permettersi un CFO/COO/CTO a tempo pieno con anni di esperienza corporate. Il modello fractional consente l'accesso a queste competenze pagando solo le giornate o i mesi effettivamente utilizzati." },
            { t: "Differenze rispetto a consulenza, recruiting e temporary management", c: "La consulenza analizza e raccomanda; il temporary management copre un ruolo per un periodo limitato. CXOORA propone una formula ibrida: presenza ricorrente, integrazione nel team, focus sui risultati e flessibilità contrattuale." },
            { t: "Come funziona la selezione", c: "Ogni manager passa una verifica di profilo, esperienze e referenze. Il matching iniziale è supportato da un algoritmo trasparente, ma la shortlist finale (max 3 profili) è sempre validata manualmente dal team CXOORA." },
            { t: "Perché il monitoraggio attraverso KPI rende il servizio più concreto", c: "Senza obiettivi misurabili è impossibile verificare il valore. La piattaforma include strumenti per impostare milestone, aggiornare KPI e visualizzare l'avanzamento nel tempo." },
            { t: "Perché non è un marketplace generalista", c: "Non offriamo centinaia di profili da valutare. Il valore di CXOORA è nel processo di analisi del bisogno e nella curatela manuale della shortlist." },
          ].map((b, i) => (
            <div key={i} className="cxo-card p-6 sm:p-8">
              <h2 className="font-display text-xl sm:text-2xl font-semibold text-slate-900">{b.t}</h2>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">{b.c}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Esempi per settore</h2>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              ["Impiantistica", "COO per coordinamento commesse e squadre."],
              ["Costruzioni", "CFO per marginalità e cash flow."],
              ["Manifattura", "CTO per digitalizzazione e KPI."],
              ["Energia", "Sustainability & Energy Manager."],
              ["Logistica", "Procurement & Supply Chain."],
              ["Servizi", "CHRO per ruoli e organizzazione."],
              ["Retail", "CMO per customer experience."],
              ["Startup", "CFO per fundraising e governance."],
              ["Aziende familiari", "GM per passaggio generazionale."],
            ].map(([t, d]) => (
              <div key={t} className="flex gap-3 cxo-card p-4">
                <CheckCircle2 size={20} className="text-[#2D6BFF] shrink-0" />
                <div>
                  <p className="font-semibold text-slate-900">{t}</p>
                  <p className="text-sm text-slate-600">{d}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex justify-center">
            <Link to="/checkup">
              <Button size="lg" className="bg-[#2D6BFF] hover:bg-[#1F57F2] text-white h-12 px-6">Avvia il check-up <ArrowRight size={16} className="ml-2" /></Button>
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
